import React, { useEffect, useState } from "react";
import { Heading, Spinner } from "@telia/teddy";
import { API_URL_GET_SBI_TOTALS } from "../../api";
import { retailerFetch } from "../../retailerFetch";
import "./SbiRapporter.less";

interface SbiTotal {
    amount: number;
    sbi: number;
    settlementDate: string;
    total: number;
    vat: number;
}

const formatCurrency = (value: number): string =>
    new Intl.NumberFormat("nb-NO", { style: "currency", currency: "NOK", maximumFractionDigits: 0 }).format(value);

const formatDate = (value: string): string => {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat("nb-NO", { year: "numeric", month: "short", day: "numeric" }).format(date);
};

const SbiRapporter: React.FC = () => {
    const [totals, setTotals] = useState<SbiTotal[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        retailerFetch(API_URL_GET_SBI_TOTALS)
            .then((res) => {
                if (!res.ok) throw new Error(`Feil ved henting av SBI-rapporter (${res.status})`);
                return res.json() as Promise<{ totals: SbiTotal[] }>;
            })
            .then((data) => setTotals(data.totals))
            .catch((err: Error) => setError(err.message))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="retailer-dashboard-wrapper">
            <Heading mb="100" as="h1" variant="title-300">
                SBI rapporter
            </Heading>

            {loading && (
                <div className="sbi-rapporter__spinner">
                    <Spinner size="lg" />
                </div>
            )}

            {!loading && error && <p className="sbi-rapporter__error">{error}</p>}

            {!loading && !error && totals.length === 0 && <p>Ingen SBI-rapporter funnet.</p>}

            {!loading && !error && totals.length > 0 && (
                <table className="sbi-rapporter__table">
                    <thead>
                        <tr>
                            <th>SBI</th>
                            <th>Oppgjørsdato</th>
                            <th>Beløp eks. mva</th>
                            <th>MVA</th>
                            <th>Totalt ink. mva</th>
                        </tr>
                    </thead>
                    <tbody>
                        {totals.map((row) => (
                            <tr key={row.sbi}>
                                <td>{row.sbi}</td>
                                <td>{formatDate(row.settlementDate)}</td>
                                <td>{formatCurrency(row.amount)}</td>
                                <td>{formatCurrency(row.vat)}</td>
                                <td>{formatCurrency(row.total)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default SbiRapporter;
